import { useEffect, useState } from "react";
import { PublicPageShell } from "./PublicPageShell";
import { listExploreTerritories, type ExploreTerritory } from "../../lib/explore-api";
import { cn } from "../cn";
import { useI18n } from "../../i18n";

/**
 * ExplorePage — MACRO #26 · Increment 3 (`#explore`).
 *
 * REAL public discovery. Reads the certified `/api/v1/publications/explore`
 * authority (current, valid, moderation-safe publications only), newest first.
 * All user-facing copy is sourced from the i18n layer (English canonical).
 */
type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; items: ExploreTerritory[] };

const DATE_LOCALE: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  "pt-br": "pt-BR",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
  ru: "ru-RU",
};

function formatDate(iso: string | null, dateLocale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(dateLocale, { year: "numeric", month: "short", day: "numeric" });
}

export function ExplorePage() {
  const { dict, t } = useI18n();
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    listExploreTerritories(24, controller.signal)
      .then((items) => setState({ status: "ready", items }))
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        if (e instanceof DOMException && e.name === "AbortError") return;
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, []);

  return (
    <PublicPageShell
      testId="explore-page"
      index="E · 03"
      eyebrow={t("explore.eyebrow")}
      title={
        <>
          {dict.explore.titleLine1}
          <br />
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: "linear-gradient(90deg,#2DB6F0 0%,#74D4FF 55%,#118FD4 100%)" }}
          >
            {dict.explore.titleHighlight}
          </span>
        </>
      }
      intro={t("explore.intro")}
    >
      {state.status === "loading" && (
        <div data-testid="explore-loading" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="neon-glass h-44 animate-pulse opacity-60" />
          ))}
        </div>
      )}

      {state.status === "error" && (
        <div
          data-testid="explore-error"
          className="neon-glass p-8 text-center max-w-[520px] mx-auto"
        >
          <div className="font-display text-[15px] font-semibold text-text-primary">
            {t("explore.error.title")}
          </div>
          <p className="mt-2 font-display text-[13px] text-text-secondary">
            {t("explore.error.body")}
          </p>
          <button
            data-testid="explore-retry"
            onClick={() => setState({ status: "loading" })}
            className="mt-5 facet-btn h-9 px-5 text-[10.5px] font-semibold tracking-[0.2em]"
          >
            {t("explore.error.retry")}
          </button>
        </div>
      )}

      {state.status === "ready" && state.items.length === 0 && (
        <div
          data-testid="explore-empty"
          className="neon-glass p-10 text-center max-w-[560px] mx-auto flex flex-col items-center gap-4"
        >
          <span aria-hidden className="inline-flex items-center justify-center h-14 w-14 rounded-xl border border-border-accent text-neon-teal bg-obsidian/60">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.4" />
              <path d="M3 9h18M9 3v18" stroke="currentColor" strokeWidth="1" opacity="0.5" />
            </svg>
          </span>
          <div className="font-hero uppercase text-[22px] leading-tight text-text-primary">
            {t("explore.empty.title")}
          </div>
          <p className="font-display text-[13.5px] leading-[1.6] text-text-secondary">
            {t("explore.empty.body")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="#grid" className="facet-btn h-9 px-5 no-underline text-[10.5px] font-semibold tracking-[0.2em] justify-center">
              {t("explore.empty.claim")}
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center h-9 px-5 font-display text-[10.5px] font-semibold tracking-[0.2em] uppercase text-text-primary no-underline pill-gradient-border"
            >
              {t("explore.empty.how")}
            </a>
          </div>
        </div>
      )}

      {state.status === "ready" && state.items.length > 0 && (
        <>
          <div className="flex items-center gap-3 mb-6">
            <span aria-hidden className="h-px w-8 bg-text-muted" />
            <span data-testid="explore-count" className="font-mono text-[10.5px] tracking-[0.24em] uppercase text-text-secondary">
              {state.items.length === 1
                ? t("explore.liveOne", { count: state.items.length })
                : t("explore.liveOther", { count: state.items.length })}
            </span>
          </div>
          <div
            data-testid="explore-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6"
          >
            {state.items.map((item) => (
              <ExploreCard key={item.identity_id} t={item} />
            ))}
          </div>
        </>
      )}
    </PublicPageShell>
  );
}

function ExploreCard({ t: item }: { t: ExploreTerritory }) {
  const { locale, t } = useI18n();
  const name = (item.display_name && item.display_name.trim()) || t("explore.untitled");
  const date = formatDate(item.published_at, DATE_LOCALE[locale] ?? "en-US");
  return (
    <a
      data-testid={`explore-card-${item.identity_id}`}
      href={`#p/${item.identity_id}`}
      className={cn(
        "group relative flex flex-col rounded-lg overflow-hidden no-underline",
        "border border-border-soft bg-obsidian/50",
        "hover:border-border-accent transition-[border-color,transform] duration-med hover:-translate-y-0.5",
      )}
    >
      <div className="relative h-28 border-b border-border-divider overflow-hidden bg-[#070b12]">
        <div
          aria-hidden
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 30% 20%, rgba(45,182,240,0.16) 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(255,71,87,0.10) 0%, transparent 55%)",
          }}
        />
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden>
          {[8, 20, 32].map((y) => (
            <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="#2DB6F0" strokeOpacity="0.1" strokeWidth="0.4" />
          ))}
          {[25, 50, 75].map((x) => (
            <line key={x} x1={x} y1="0" x2={x} y2="40" stroke="#2DB6F0" strokeOpacity="0.1" strokeWidth="0.4" />
          ))}
          <rect x="34" y="12" width="32" height="16" rx="1.5" fill="none" stroke="#2DB6F0" strokeOpacity="0.55" strokeWidth="0.8" />
        </svg>
        <div className="absolute top-2.5 right-2.5 flex gap-1.5">
          {item.has_3d && (
            <span data-testid={`explore-card-${item.identity_id}-3d`} className="font-mono text-[8.5px] tracking-[0.14em] uppercase text-neon-teal border border-border-accent rounded-sm px-1.5 py-0.5 bg-obsidian/70">
              3D
            </span>
          )}
          {item.has_media && (
            <span className="font-mono text-[8.5px] tracking-[0.14em] uppercase text-text-secondary border border-border-soft rounded-sm px-1.5 py-0.5 bg-obsidian/70">
              {t("explore.badgeMedia")}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col flex-1 p-4 gap-2">
        <h2
          data-testid={`explore-card-${item.identity_id}-name`}
          className="font-display text-[14px] font-semibold tracking-[0.02em] text-text-primary truncate group-hover:text-neon-teal transition-colors duration-fast"
        >
          {name}
        </h2>
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.12em] uppercase text-text-muted">
          <span>
            {item.module_count === 1
              ? t("explore.modulesOne", { count: item.module_count })
              : t("explore.modulesOther", { count: item.module_count })}
          </span>
          {date && (
            <>
              <span aria-hidden className="h-2.5 w-px bg-border-divider" />
              <span>{date}</span>
            </>
          )}
        </div>
        <span className="mt-1 inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold tracking-[0.16em] uppercase text-neon-teal">
          {t("explore.view")}
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path d="M3 6h6m0 0L6 3m3 3L6 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </a>
  );
}
